import type { GeneralProductionResponse, GeneralProductionShift } from "../../../shared/general-production";
import type { GeneralProductionFilters } from "./general-production-model";

export type GeneralProductionViewState = {
  planId: string;
  shift: GeneralProductionShift | "ALL";
  filters: GeneralProductionFilters;
  hideCompleted: boolean;
};
export const defaultGeneralProductionView = (): GeneralProductionViewState => ({
  planId: "", shift: "ALL", filters: { line: "ALL", date: "", unit: "ALL", search: "" }, hideCompleted: false,
});
const keys = ["gpPlan", "gpShift", "gpLine", "gpDate", "gpUnit", "gpSearch", "gpHideCompleted"];
const safeText = (value: string, limit: number) => value.length <= limit && Array.from(value).every((character) => character.charCodeAt(0) >= 32 && character.charCodeAt(0) !== 127);
const validDate = (value: string) => /^\d{4}-\d{2}-\d{2}$/u.test(value)
  && !Number.isNaN(Date.parse(`${value}T12:00:00Z`))
  && new Date(`${value}T12:00:00Z`).toISOString().slice(0, 10) === value;

export function readGeneralProductionView(search: string) {
  const view = defaultGeneralProductionView();
  const params = new URLSearchParams(search);
  let invalid = false;
  const take = (key: string, valid: (value: string) => boolean, apply: (value: string) => void) => {
    const values = params.getAll(key);
    if (!values.length) return;
    const value = values[0];
    if (values.length !== 1 || !valid(value)) { invalid = true; return; }
    apply(value);
  };
  take("gpPlan", (v) => !!v && safeText(v, 100), (v) => { view.planId = v; });
  take("gpShift", (v) => ["ALL", "VR1", "VR2", "VR3"].includes(v), (v) => {
    if (v === "ALL" || v === "VR1" || v === "VR2" || v === "VR3") view.shift = v;
  });
  take("gpLine", (v) => !!v && safeText(v, 200), (v) => { view.filters.line = v; });
  take("gpDate", (v) => v === "ALL" || validDate(v), (v) => { view.filters.date = v; });
  take("gpUnit", (v) => ["ALL", "kg", "koli", "adet", "UNKNOWN"].includes(v), (v) => { view.filters.unit = v; });
  take("gpSearch", (v) => safeText(v, 200), (v) => { view.filters.search = v; });
  take("gpHideCompleted", (v) => v === "1" || v === "0", (v) => { view.hideCompleted = v === "1"; });
  return { view, notice: invalid ? "Bağlantıdaki geçersiz seçimler varsayılana döndürüldü." : "" };
}

export function writeGeneralProductionView(search: string, view: GeneralProductionViewState) {
  const params = new URLSearchParams(search);
  keys.forEach((key) => params.delete(key));
  const values = [view.planId, view.shift, view.filters.line, view.filters.date, view.filters.unit, view.filters.search, view.hideCompleted ? "1" : "0"];
  keys.forEach((key, index) => { if (values[index]) params.set(key, values[index]); });
  return params.toString();
}

export function reconcileGeneralProductionView(view: GeneralProductionViewState, data: GeneralProductionResponse, today: string) {
  const filters = { ...view.filters };
  const messages: string[] = [];
  if (filters.line !== "ALL" && !data.lines.some((line) => line.key === filters.line)) {
    filters.line = "ALL"; messages.push("Seçili hat bu planda bulunamadı; tüm hatlar gösteriliyor.");
  }
  const plan = data.selectedPlan;
  if (!filters.date) filters.date = plan && today >= plan.weekStart && today <= plan.weekEnd ? today : "ALL";
  else if (filters.date !== "ALL" && (!plan || filters.date < plan.weekStart || filters.date > plan.weekEnd)) {
    filters.date = "ALL"; messages.push("Seçili gün bu haftada bulunamadı; tüm günler gösteriliyor.");
  }
  return { view: { ...view, filters }, notice: messages.join(" ") };
}
